import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { LEADetails } from '../../../../types/admin';
import { AdminUserDetails } from '../../../../types/user';
import { formatSeconds, minifyBytes } from '../../../../utils/conversion';
import { removeOutliers } from '../../../../utils/stats';

dayjs.extend(isBetween);

const formatGB = (totalBytes: number): string => {
	if (isNaN(totalBytes) || totalBytes === 0 || totalBytes === -Infinity) {
		return 'No results';
	}
	return `${minifyBytes(totalBytes)}`;
};

export const excludedForces = process.env.NODE_ENV === 'development' ? [] : ['Prometheus Development Team', 'Prometheus Demo Team', 'ACE'];

export const processStats = (
	files: Record<string, string>[],
	users: AdminUserDetails[],
	leas: LEADetails,
	operations: Record<string, string>[],
	leaFilter: string,
	filterFrom: Dayjs | null,
	filterTo: Dayjs
): string[][] => {
	// data cleanup
	const leasToDisplay = Object.fromEntries(
		Object.entries(leas).filter(([key]) => (!excludedForces.includes(key) && leaFilter ? key === leaFilter : key))
	);

	const usersToDisplay = users.filter(
		(user) => user.organisations && user.organisations.some((org) => Object.keys(leasToDisplay).includes(org.name))
	);

	const filesToDisplay = files.filter(
		(file) => file['organisations'] && file['organisations'].split(', ').some((org) => Object.keys(leasToDisplay).includes(org))
	);

	// docs processed
	const filesProcessed = filesToDisplay
		.filter((file) => file.processingComplete && file.processingComplete !== 'Did Not Finish Processing')
		.filter((file) =>
			filterFrom !== null && filterFrom.isValid() && filterFrom.year() >= 2025
				? dayjs(file.processingComplete.replace(',', '.').replace(' ', 'T')).isBetween(filterFrom, filterTo, 'd', '[]')
				: filterFrom === null
		);

	// remove duplicates
	const uniqueMap = new Map(filesProcessed.map((file) => [`${file.fileName}-${file.documentsProcessed}`, file]));
	const uniqueFiles = Array.from(uniqueMap.values());

	const docsProcessed = uniqueFiles.flatMap((file) => Number(file.documentsProcessed));
	const totalDocsProcessed = docsProcessed.reduce((prev, acc) => {
		return acc + prev;
	}, 0);

	// files uploaded
	const filesUploaded = filesToDisplay
		.filter((file) => file.uploadComplete && file.uploadComplete !== 'No File Uploaded')
		.filter((file) =>
			filterFrom !== null
				? dayjs(file.uploadComplete.replace(',', '.').replace(' ', 'T')).isBetween(filterFrom, filterTo, 'd', '[]')
				: filterFrom === null
		);

	// users onboarded
	const usersOnboarded = usersToDisplay
		.filter((item) => item.firstSignIn !== undefined)
		.filter((item) =>
			filterFrom !== null ? dayjs(item.firstSignIn).isBetween(filterFrom, filterTo, 'd', '[]') : filterFrom === null
		).length;

	// leas onboarded
	const leasOnboarded = Object.entries(leasToDisplay)
		.filter((item) => item[1].createdAt !== undefined)
		.filter((item) =>
			filterFrom !== null ? dayjs(item[1].createdAt).isBetween(filterFrom, filterTo, 'd', '[]') : filterFrom === null
		).length;

	const allUploadSizes = filesUploaded
		.filter((file) => file.uploadSizeBytes && file.uploadSizeBytes !== 'N/A')
		.flatMap((file) => Number(file.uploadSizeBytes));
	const cleanUploadSizes = removeOutliers(allUploadSizes);
	const totalUploadSize = cleanUploadSizes.reduce((prev, acc) => {
		return acc + prev;
	}, 0);

	const allUploadTimes = filesUploaded
		.filter((file) => file.uploadTimeSeconds && file.uploadTimeSeconds !== 'N/A')
		.flatMap((file) => Number(file.uploadTimeSeconds));
	const cleanUploadTimes = removeOutliers(allUploadTimes);
	const totalUploadTime = cleanUploadTimes.reduce((prev, acc) => {
		return prev + acc;
	}, 0);

	const allProcessingTimes = filesProcessed
		.filter((file) => file.processingTimeSeconds && file.processingTimeSeconds !== 'N/A')
		.flatMap((file) => Number(file.processingTimeSeconds));
	const cleanProcessingTimes = removeOutliers(allProcessingTimes);
	const totalProcessingTime = cleanProcessingTimes.reduce((prev, acc) => {
		return prev + acc;
	}, 0);

	const largestOperation =
		operations.filter((op) => (leaFilter ? op['organisations'] && op['organisations'].split(', ').includes(leaFilter) : op)).length !== 0 &&
		operations.reduce((max, current) => {
			return current.value > max.numDocs ? current : max;
		});

	const headlineRow = [totalDocsProcessed.toString(), filesUploaded.length.toString(), usersOnboarded.toString(), leasOnboarded.toString()];
	const dataRow = [formatGB(totalUploadSize / allUploadSizes.length), formatGB(Math.max(...allUploadSizes)), formatGB(totalUploadSize)];
	const timeRow = [formatSeconds(totalUploadTime / allUploadTimes.length), formatSeconds(totalProcessingTime / allProcessingTimes.length)];
	const opRow =
		operations.length === 0
			? ['None found']
			: [
					largestOperation
						? `${(largestOperation as Record<string, string>).operation || 'Unknown Operation Name'},
			${(largestOperation as Record<string, string>).jobs || 'Unknown'} devices,
			${(largestOperation as Record<string, string>).numUsers || 'Unknown'} users`
						: 'No results',
				];

	return [headlineRow, dataRow, timeRow, opRow];
};

export const statsHeaders = (
	chartIndex: number | null,
	chartData: {
		label: string;
	}[]
): string[][] => {
	const headlineRow = [
		chartIndex !== null ? chartData[chartIndex].label : 'Messages Processed',
		chartIndex !== null ? chartData[chartIndex].label : 'Files Uploaded and Processed',
		chartIndex !== null ? chartData[chartIndex].label : 'Users Onboarded',
		chartIndex !== null ? chartData[chartIndex].label : 'Forces Onboarded',
	];

	const sizeRow = ['Average Upload Size', 'Largest Upload Size', 'Total Data Processed'];

	const timeRow = ['Average Upload Time', 'Average Processing Time'];

	return [headlineRow, sizeRow, timeRow, ['Largest Operation']];
};

export const processChartStats = (
	files: Record<string, string>[],
	users: AdminUserDetails[],
	leas: LEADetails,
	leaFilter: string,
	filterFrom: Dayjs | null,
	filterTo: Dayjs
): number[][][] => {
	const leasToDisplay = Object.fromEntries(
		Object.entries(leas).filter(([key]) => (!excludedForces.includes(key) && leaFilter ? key === leaFilter : key))
	);

	const usersToDisplay = users.filter(
		(user) => user.organisations && user.organisations.some((org) => Object.keys(leasToDisplay).includes(org.name))
	);
	const filesToDisplay = files.filter(
		(file) => file['organisations'] && file['organisations'].split(', ').some((org) => Object.keys(leasToDisplay).includes(org))
	);

	const now = new Date();
	const docsProcessedByMonth = [];
	const filesUploadedByMonth = [];
	const usersByMonth = [];
	const leasByMonth = [];

	const numWeeks = filterFrom === null ? 6 : filterTo.diff(filterFrom.subtract(1, 'w'), 'week');

	for (let i = 0; i < numWeeks; i++) {
		const granularity = filterFrom === null ? 'month' : 'week';
		const targetDate =
			filterFrom === null ? new Date(now.getFullYear(), now.getMonth() - i, 1) : filterFrom?.startOf('week').add(i, 'week');

		const uniqueMap = new Map(filesToDisplay.map((file) => [`${file.fileName}-${file.documentsProcessed}`, file]));
		const uniqueFiles = Array.from(uniqueMap.values());

		const uniqueFilteredFiles = uniqueFiles
			.filter((item) => item.processingComplete && item.documentsProcessed && item.processingComplete != 'Did Not Finish Processing')
			.filter((item) => dayjs(item.processingComplete.replace(',', '.').replace(' ', 'T')).isSame(targetDate, granularity));

		const docsProcessed = uniqueFilteredFiles.flatMap((file) => Number(file.documentsProcessed));
		const totalDocs = docsProcessed.reduce((prev, acc) => {
			return acc + prev;
		}, 0);

		const totalFiles = filesToDisplay.filter(
			(item) =>
				item.uploadComplete &&
				item.uploadComplete !== 'No File Uploaded' &&
				dayjs(item.uploadComplete.replace(',', '.').replace(' ', 'T')).isSame(targetDate, granularity)
		);

		const totalUsers = usersToDisplay
			.filter((item) => item.firstSignIn !== undefined)
			.filter((item) => dayjs(item.firstSignIn).isSame(targetDate, granularity));

		const totalLeas = Object.entries(leasToDisplay)
			.filter((item) => item[1].createdAt !== undefined)
			.filter((item) => dayjs(item[1].createdAt).isSame(targetDate, granularity));

		if (filterFrom === null) {
			leasByMonth.unshift(totalLeas.length);
			usersByMonth.unshift(totalUsers.length);
			filesUploadedByMonth.unshift(totalFiles.length);
			docsProcessedByMonth.unshift(totalDocs);
		} else {
			leasByMonth.push(totalLeas.length);
			usersByMonth.push(totalUsers.length);
			filesUploadedByMonth.push(totalFiles.length);
			docsProcessedByMonth.push(totalDocs);
		}
	}

	return [[docsProcessedByMonth, filesUploadedByMonth, usersByMonth, leasByMonth]];
};
